import React, { Component } from 'react';
import { isEqual } from 'lodash';
import cx from 'classnames';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Popover, { PopoverReference } from '@mui/material/Popover';
import Switch from '@mui/material/Switch';
import { List } from '@mui/material';
import { connect } from 'react-redux';
import { Button, Tooltip, TTDatePicker, DateWrapper } from '@trucktrax/trucktrax-common';
import { RegionDto } from '@trucktrax/trucktrax-ts-common';
import styles from './MapFilters.module.css';
import {
  resetMapFilters,
  toggleMapFilter,
  setOpenMapFilters,
} from '../../../store/actions/mapFilterActions';
import { getIdFromUrl, noop } from '../../../util/appUtil';
import {
  getUserPreferences,
  updateUserPreferences,
} from '../../../services/userPreferencesService';
import { MapFilterState, PlantFilterOption } from '../../../store/reducers/mapFilterReducers';
import { isFeatureFlagEnabled } from '../../../util/featureFlagUtil';
import { TRUCKTRAX_GEO_FLAG } from '../../../constants/appConstants';
import { RegionAccessMap } from '../../../util/permissionUtil';
import { SELECTED_PLANT_URLS } from '../../../constants/localStorageConstants';
import { ReduxState } from '../../../store';
import { incomingMapFilterPlantListSelector } from '../../../store/selectors/incomingMaterialsSelectors';
import {
  buildIncomingPlantFilters,
  clearIncomingPlantSelection,
  mergeIncomingDeselectedPlantIds,
  readIncomingDeselectedPlantIds,
} from '../../../util/incomingMaterialsPreferencesUtil';
import { GeotraxUserPreferencesDto } from '../../../types';

export class MapFiltersDropdown extends Component<
  MapFiltersDropdownProps,
  MapFiltersDropdownState
> {
  static defaultProps = {
    toggleMapFilter: noop,
    resetMapFilters: noop,
    setOpenMapFilters: noop,
  };

  mapFilterButtonRef = React.createRef<HTMLButtonElement>();

  // DEV-4973: incoming saves and the incoming Reset clear each re-read the preferences before
  // their PUT and are not serialised, so two quick clicks can resolve out of order and the
  // older PUT would land last, dropping the newer click on the server. Each one takes a
  // token, and a response only applies if no newer incoming save or clear has started.
  incomingSaveSeq = 0;

  constructor(props: MapFiltersDropdownProps) {
    super(props);
    this.state = {
      anchorElement: null,
      anchorReference: 'anchorPosition',
      anchorPosition: {
        top: 75,
        left: 318,
      },
      isOpen: false,
      startDate: props.mapFilters.ordersStartDate || DateWrapper.now.startOfDay.date,
      endDate: props.mapFilters.ordersEndDate || DateWrapper.now.startOfDay.date,
    };
  }

  onOpen = () => {
    const target = this.mapFilterButtonRef.current!;
    this.setState({
      anchorElement: target,
      isOpen: true,
    });
    this.props.setOpenMapFilters(true);
  };

  onClose = () => {
    this.setState({
      anchorElement: null,
      isOpen: false,
    });
    this.props.setOpenMapFilters(false);
  };

  onChange = (name: string) => (event: any) => {
    this.props.toggleMapFilter!({
      [name]: event.target.checked,
      shouldSave: true,
    });
  };

  isIncomingVariant = (): boolean => this.props.variant === 'incoming';

  onChangePlants = (id: number) => {
    const list = this.props.mapFilters.plantList.map((plant: PlantFilterOption) => {
      if (plant.id === id) {
        return {
          ...plant,
          isSelected: !plant.isSelected,
        };
      }
      return plant;
    });
    if (this.isIncomingVariant()) {
      // DEV-3820: the incoming list spans every product line in the region, so it gets its
      // own slice and stays out of mapFilters[region][productLine], which would be polluted
      // by plants from other product lines. DEV-4973: it is saved instead under the
      // region-keyed incomingMaterials preference (see the save in componentDidUpdate).
      this.props.toggleMapFilter!({
        incomingPlantFilters: list,
        shouldSave: true,
      });
      return;
    }
    this.props.toggleMapFilter!({
      plantList: list,
      shouldSave: true,
    });
  };

  onReset = async () => {
    if (this.isIncomingVariant()) {
      // DEV-3820: unlike the main map's reset (resetSavedMapFilters), this does not wipe the
      // saved filters of every region and product line. It puts the plant selection back to
      // its all-checked default and turns geofences off. showPlantsGeofences is shared with
      // the main map, so turning it off here applies there too and is saved to the current
      // [region][productLine], the same as the incoming geofence checkbox (the save guard
      // keeps selectedPlantIds out).
      // DEV-4973: the clear is awaited before the dispatch, as the main map's reset awaits
      // resetSavedMapFilters: the dispatch sets shouldSave, and that save takes a newer
      // sequence number than the clear, so running them concurrently would drop one of them.
      await this.clearSavedIncomingPlantSelection();
      this.props.toggleMapFilter!({
        incomingPlantFilters: [],
        showPlantsGeofences: false,
        shouldSave: true,
        // The user's intent is explicit and the clear removed the saved entry, so the
        // all-checked list is now the real selection.
        incomingPlantFiltersUnverified: false,
      });
      return;
    }
    this.setState(
      {
        startDate: DateWrapper.now.startOfDay.date,
        endDate: DateWrapper.now.startOfDay.date,
      },
      async () => {
        await this.resetSavedMapFilters();
        this.props.resetMapFilters!();
      },
    );
  };

  minDate = DateWrapper.now.startOfDay.date;

  maxDate = DateWrapper.now.addDays(7).endOfDay.date;

  onStartDateChange = (event: Date) => {
    const { minDate, maxDate } = this;

    if (DateWrapper.isValidDate(event) && new DateWrapper(event).isBetween(minDate, maxDate)) {
      const { ordersEndDate } = this.props.mapFilters;
      if (event > ordersEndDate) {
        this.setState({ endDate: event }, () => {
          this.props.toggleMapFilter!({
            ordersEndDate: event,
          });
        });
      }

      this.setState({ startDate: event }, () => {
        this.props.toggleMapFilter!({
          ordersStartDate: event,
        });
      });
    } else {
      this.setState(prevState => ({ startDate: new Date(prevState.startDate) }));
    }
  };

  onEndDateChange = (event: any) => {
    const { minDate, maxDate } = this;

    if (DateWrapper.isValidDate(event) && new DateWrapper(event).isBetween(minDate, maxDate)) {
      const { ordersStartDate } = this.props.mapFilters;
      if (ordersStartDate > event) {
        this.setState({ startDate: event }, () => {
          this.props.toggleMapFilter!({
            ordersStartDate: event,
          });
        });
      }

      this.setState({ endDate: event }, () => {
        this.props.toggleMapFilter!({
          ordersEndDate: event,
        });
      });
    } else {
      this.setState(prevState => ({ endDate: new Date(prevState.endDate) }));
    }
  };

  // The MaterialUI/DatePicker doesn't fire an onChanged event if
  //  the date text field is blank, so handle the blank date case here.
  // Note: The Blur event fires before the onChanged event
  onStartDateBlur = (event: any) => {
    if (event.target.value === '') {
      event.target.value = this.state.startDate;
    }
  };

  onEndDateBlur = (event: any) => {
    if (event.target.value === '') {
      event.target.value = this.state.endDate;
    }
  };

  fetchUserPreferences = async () => {
    const userPreferences = await getUserPreferences(this.props.currentUserUrl);

    if (userPreferences) {
      this.setState({
        userPreferences,
      });
    }
  };

  componentDidMount(): void {
    this.persistSelectedPlantUrlsToLocalStorage();
  }

  persistSelectedPlantUrlsToLocalStorage(): void {
    // DEV-3820: SELECTED_PLANT_URLS belongs to the main map's product-line-scoped plant
    // list. The incoming variant renders a region-wide list, so writing it here would
    // overwrite the main map's saved selection with plants from other product lines.
    if (this.isIncomingVariant()) return;
    const selectedPlants = this.props.mapFilters.plantList
      .filter(plant => plant.isSelected && !!plant.url)
      .map(plant => plant.url!);
    MapFiltersDropdown.saveSelectedPlantUrlsToLocalStorage(selectedPlants);
  }

  async componentDidUpdate(prevProps: MapFiltersDropdownProps) {
    if (!this.state.userPreferences) {
      await this.fetchUserPreferences();
      return;
    }

    if (this.props.mapFilters.isOpen && prevProps.mapFilters.isOpen === false) {
      this.onOpen();
    }

    if (this.props.mapFilters.shouldSave && !isEqual(prevProps.mapFilters, this.props.mapFilters)) {
      if (this.isIncomingVariant()) {
        await this.saveIncomingPreferences();
      } else {
        const preferences = this.buildMapFiltersPreferences(this.state.userPreferences, true);

        const updatedUserPreferences = await updateUserPreferences(
          this.props.currentUserUrl,
          preferences,
        );

        if (updatedUserPreferences) {
          this.setState(
            {
              userPreferences: updatedUserPreferences,
            },
            () => {
              this.props.toggleMapFilter!({
                shouldSave: false,
              });
            },
          );
        }
      }
    }

    const shouldUpdateSelectedPlantUrlsLocalStorage = !isEqual(
      prevProps.mapFilters.plantList,
      this.props.mapFilters.plantList,
    );
    if (shouldUpdateSelectedPlantUrlsLocalStorage) {
      this.persistSelectedPlantUrlsToLocalStorage();
    }
  }

  static saveSelectedPlantUrlsToLocalStorage(selectedPlantUrls: string[]): void {
    const urls = selectedPlantUrls ?? [];
    window.localStorage.setItem(SELECTED_PLANT_URLS, JSON.stringify(urls));
  }

  // Writes the toggles into mapFilters[region][productLine] of a copy of basePreferences.
  // `source` defaults to the current props; the incoming save passes the values it started
  // with, so a change that lands during its re-read cannot leak in.
  private buildMapFiltersPreferences(
    basePreferences: GeotraxUserPreferencesDto | undefined,
    includeSelectedPlantIds: boolean,
    source: MapFiltersPreferencesSource = this.props,
  ): GeotraxUserPreferencesDto {
    const currentRegionId = source.currentRegion.id;
    const currentProductLine = source.selectedProductLine;

    const existingPreferences = { ...basePreferences };
    const existingMapFilters = { ...existingPreferences.mapFilters };
    const existingMapFiltersForRegion = existingMapFilters[currentRegionId] || {};
    const existingMapFiltersForRegionandProductLine =
      existingMapFiltersForRegion[currentProductLine] || {};
    const {
      showTrucks,
      showPlants,
      showPlantsGeofences,
      showJobsites,
      showJobsitesGeofences,
      plantList,
      showExtTrucks,
    } = source.mapFilters;

    // DEV-3820: the incoming variant's list spans every product line in the region, so it
    // must not be written into these [region][productLine]-keyed preferences. Omitting
    // the key leaves whatever the main map already saved intact.
    const selectedPlantIdsPreference = includeSelectedPlantIds
      ? { selectedPlantIds: plantList.filter(plant => plant.isSelected).map(plant => plant.id) }
      : {};
    // ensure we don't overwrite map filters for combinations of region & product line other than the current one
    // also ensure we don't overwrite preferences other than map filters, if/when we add more preferences
    return {
      ...existingPreferences,
      mapFilters: {
        ...existingMapFilters,
        [currentRegionId]: {
          ...existingMapFiltersForRegion,
          [currentProductLine]: {
            ...existingMapFiltersForRegionandProductLine,
            showTrucks,
            showExtTrucks,
            showPlants,
            showPlantsGeofences,
            showJobsites,
            showJobsitesGeofences,
            ...selectedPlantIdsPreference,
          },
        },
      },
    };
  }

  // DEV-4973: the incoming variant's save. The toggles go to mapFilters[region][productLine]
  // without selectedPlantIds (DEV-3820); the plant selection goes to the region-keyed
  // incomingMaterials preference.
  saveIncomingPreferences = async () => {
    this.incomingSaveSeq += 1;
    const requestedSeq = this.incomingSaveSeq;
    // The values this save was triggered with; see buildMapFiltersPreferences.
    const { currentRegion, selectedProductLine, mapFilters, currentUserUrl } = this.props;
    const { plantList, incomingPlantFiltersUnverified } = mapFilters;
    // mapFilters stays keyed by currentRegion.id (the main map's existing key, deliberately
    // unchanged); incomingMaterials is keyed by getIdFromUrl(currentRegion.url) to match its
    // reader (IncomingMaterialsView), plantsService and TrafficBtn.
    const currentRegionKey = getIdFromUrl(currentRegion.url);

    // Every IM plant click saves, and TrafficBtn on the same page writes viewLiveTraffic on
    // its own. Re-read before writing so this save does not spread a stale cached copy over
    // it; fall back to the cache when the read fails. The main map keeps its cached behaviour.
    const freshPreferences = await getUserPreferences(currentUserUrl);
    const basePreferences = freshPreferences ?? this.state.userPreferences;
    // A newer incoming save (built from newer props) or a Reset clear has started: drop this
    // one before its PUT.
    if (!this.isIncomingSaveStillCurrent(requestedSeq)) return;

    const preferences = this.buildMapFiltersPreferences(basePreferences, false, {
      currentRegion,
      selectedProductLine,
      mapFilters,
    });

    // The plant selection goes to incomingMaterials[region].deselectedPlantIds — the unchecked
    // ids, so a plant created later or newly in scope comes up checked (PO decision 2026-10-09)
    // — merged with the saved list so plants not loaded right now keep their saved state. An
    // empty list (plants not loaded, fetch failed, no product lines, or a region change while a
    // failed PUT left shouldSave on) is skipped so it cannot wipe the saved selection.
    //
    // If the list was hydrated from a failed read (incomingPlantFiltersUnverified), plants the
    // user did not touch are shown checked whatever is saved, so the selection write is held
    // back until a read succeeds: with a fresh read the merge only adds the unchecked ids, and
    // without one (the cache may be stale) only the toggles are written and the flag stays set.
    const writeSelection =
      plantList.length > 0 && (!incomingPlantFiltersUnverified || freshPreferences !== undefined);
    const preferencesToSave = writeSelection
      ? mergeIncomingDeselectedPlantIds(
          preferences,
          currentRegionKey,
          plantList.map(plant => plant.id),
          plantList.filter(plant => !plant.isSelected).map(plant => plant.id),
          { trustDisplay: !incomingPlantFiltersUnverified },
        )
      : preferences;

    const updatedUserPreferences = await updateUserPreferences(currentUserUrl, preferencesToSave);
    // Superseded while the PUT was in flight — the newer save owns the cache and the
    // shouldSave reset.
    if (!this.isIncomingSaveStillCurrent(requestedSeq)) return;

    if (updatedUserPreferences) {
      this.setState(
        {
          userPreferences: updatedUserPreferences,
        },
        () => {
          if (incomingPlantFiltersUnverified && writeSelection) {
            // The selection was merged against a successful read: show the real state now
            // and drop the flag, in the same dispatch that clears shouldSave.
            this.props.toggleMapFilter!({
              incomingPlantFilters: buildIncomingPlantFilters(
                plantList,
                readIncomingDeselectedPlantIds(preferencesToSave, currentRegionKey),
              ),
              incomingPlantFiltersUnverified: false,
              shouldSave: false,
            });
            return;
          }
          this.props.toggleMapFilter!({
            shouldSave: false,
          });
        },
      );
    }
  };

  resetSavedMapFilters = async () => {
    if (!this.state.userPreferences) {
      return;
    }

    const existingPreferences = { ...this.state.userPreferences };

    const preferences: GeotraxUserPreferencesDto = {
      ...existingPreferences,
      // clear out mapFilters completely (for all regions & product lines)
      mapFilters: {},
    };

    const updatedUserPreferences = await updateUserPreferences(
      this.props.currentUserUrl,
      preferences,
    );
    if (updatedUserPreferences) {
      this.setState({
        userPreferences: updatedUserPreferences,
      });
    }
  };

  // DEV-4973: the incoming variant's counterpart to resetSavedMapFilters — removes only the
  // current region's saved Destination Plants selection and leaves mapFilters alone. It is an
  // explicit clear because the merge keeps out-of-scope unchecked ids, which Reset must drop.
  clearSavedIncomingPlantSelection = async () => {
    this.incomingSaveSeq += 1;
    const requestedSeq = this.incomingSaveSeq;
    const regionKey = getIdFromUrl(this.props.currentRegion.url);

    // Re-read first: the cached copy can be stale.
    const existingPreferences =
      (await getUserPreferences(this.props.currentUserUrl)) ?? this.state.userPreferences;
    if (!this.isIncomingSaveStillCurrent(requestedSeq)) {
      return;
    }
    // No entry for the region: it is already on the all-checked default, so skip the PUT. An
    // entry without deselectedPlantIds (stale keys only) is still removed.
    if (!existingPreferences || existingPreferences.incomingMaterials?.[regionKey] === undefined) {
      return;
    }

    const preferences = clearIncomingPlantSelection(existingPreferences, regionKey);

    const updatedUserPreferences = await updateUserPreferences(
      this.props.currentUserUrl,
      preferences,
    );
    if (!this.isIncomingSaveStillCurrent(requestedSeq)) {
      return;
    }
    if (updatedUserPreferences) {
      this.setState({
        userPreferences: updatedUserPreferences,
      });
    }
  };

  // DEV-4973: same shape as IncomingMaterialsSubscription.isSeedStillCurrent.
  isIncomingSaveStillCurrent = (requestedSeq: number): boolean =>
    this.incomingSaveSeq === requestedSeq;

  render() {
    const minDate = this.minDate ? new DateWrapper(this.minDate).toUSDateString() : undefined;
    const maxDate = this.maxDate ? new DateWrapper(this.maxDate).toUSDateString() : undefined;

    const {
      wasFilterOverridden,
      plantList,
      showTrucks,
      showPlants,
      showJobsites,
      showPlantsGeofences,
      showJobsitesGeofences,
      showExtTrucks,
    } = this.props.mapFilters;

    return (
      <div className={styles.wrapper}>
        <Tooltip text="Map Filters">
          <div>
            {/* red dot */}
            {wasFilterOverridden ? <span className={styles.dot} /> : null}
            <button
              ref={this.mapFilterButtonRef}
              className={cx(styles.button, {
                [styles.isOpen]: this.state.isOpen,
              })}
              onClick={this.onOpen}
              data-test="map-filter-button"
            >
              <i className={cx('icon-map-filters', styles.icon)} />
            </button>
          </div>
        </Tooltip>
        <Popover
          classes={{ paper: styles.paper }}
          open={this.state.isOpen}
          anchorEl={this.state.anchorElement}
          onClose={this.onClose}
          anchorPosition={this.state.anchorPosition}
          anchorReference={this.state.anchorReference}
          // autoFocus={false} // TODO
        >
          {/* Header with arrow */}
          <div className={styles.header}>
            <span className={styles.headerText}>MAP FILTERS</span>
            <span className={styles.arrow} />
          </div>
          <FormGroup classes={{ root: styles.formGroup }}>
            <div className={styles.subtitle}>
              {this.isIncomingVariant() ? 'Destination Plants' : 'Plants'}
            </div>
            <Divider className={styles.topDivider} />
            <div className={styles.listContainer}>
              <List className={styles.list}>
                {plantList.map((item: any) => (
                  <FormControlLabel
                    key={item.id}
                    className={cx(styles.formControlSectionEnd)}
                    classes={{
                      label: styles.controlLabelCheckboxPlants,
                      disabled: styles.controlLabelDisabled,
                    }}
                    control={
                      <Checkbox
                        checked={item.isSelected}
                        onChange={() => this.onChangePlants(item.id)}
                        color="primary"
                        classes={{ disabled: styles.controlLabelDisabled }}
                        className={styles.controlCheckboxPlants}
                      />
                    }
                    label={item.name}
                    data-test="map-filter-plants-geofences-checkbox"
                  />
                ))}
              </List>
            </div>
          </FormGroup>
          {this.isIncomingVariant() ? (
            <FormGroup classes={{ root: styles.formGroup }}>
              <div className={styles.subtitle}>Items on Map</div>
              <Divider className={styles.topDivider} />
              <FormControlLabel
                className={styles.formControlSectionEnd}
                classes={{
                  label: styles.checkboxControlLabel,
                  disabled: styles.controlLabelDisabled,
                }}
                control={
                  <Checkbox
                    checked={showPlantsGeofences}
                    onChange={this.onChange('showPlantsGeofences')}
                    color="primary"
                    classes={{ disabled: styles.controlLabelDisabled }}
                  />
                }
                label="Show geofences"
                data-test="map-filter-plants-geofences-checkbox"
              />
            </FormGroup>
          ) : (
            <FormGroup classes={{ root: styles.formGroup }}>
              <div className={styles.subtitle}>Items on Map</div>
              <Divider className={styles.topDivider} />
              <FormControlLabel
                className={styles.formControlLabel}
                classes={{
                  label: styles.controlLabel,
                  disabled: styles.controlLabelDisabled,
                }}
                control={
                  <Switch
                    value="PlantsSwitch"
                    checked={showPlants}
                    onChange={e => {
                      this.onChange('showPlants')(e);
                      this.props.toggleMapFilter!({
                        showPlantsGeofences: false,
                      });
                    }}
                    color="primary"
                  />
                }
                label="Plants"
                data-test="map-filter-plants-switch"
              />
              <FormControlLabel
                className={cx(styles.indentedFormControlLabel, styles.formControlSectionEnd)}
                classes={{
                  label: styles.checkboxControlLabel,
                  disabled: styles.controlLabelDisabled,
                }}
                control={
                  <Checkbox
                    checked={showPlantsGeofences}
                    onChange={this.onChange('showPlantsGeofences')}
                    disabled={!showPlants}
                    color="primary"
                    classes={{ disabled: styles.controlLabelDisabled }}
                  />
                }
                label="Show geofences"
                data-test="map-filter-plants-geofences-checkbox"
              />
              <FormControlLabel
                className={styles.formControlLabel}
                classes={{
                  label: styles.controlLabel,
                  disabled: styles.controlLabelDisabled,
                }}
                control={
                  <Switch
                    value="TrucksSwitch"
                    checked={showTrucks}
                    onChange={this.onChange('showTrucks')}
                    color="primary"
                  />
                }
                label="Internal Trucks"
                data-test="map-filter-trucks-switch"
              />
              {isFeatureFlagEnabled(
                this.props.userPermission,
                this.props.currentRegion?.url ?? '',
                TRUCKTRAX_GEO_FLAG,
              ) && (
                <FormControlLabel
                  className={styles.formControlLabel}
                  classes={{
                    label: styles.controlLabel,
                    disabled: styles.controlLabelDisabled,
                  }}
                  control={
                    <Switch
                      value="ExtTrucksSwitch"
                      checked={showExtTrucks}
                      onChange={this.onChange('showExtTrucks')}
                      color="primary"
                    />
                  }
                  label="External Trucks"
                  data-test="map-filter-trucks-switch"
                />
              )}
              <FormControlLabel
                className={styles.formControlLabel}
                classes={{
                  label: styles.controlLabel,
                  disabled: styles.controlLabelDisabled,
                }}
                control={
                  <Switch
                    value="OrdersSwitch"
                    checked={showJobsites}
                    onChange={e => {
                      this.onChange('showJobsites')(e);
                      this.props.toggleMapFilter!({
                        showJobsitesGeofences: false,
                      });
                    }}
                    color="primary"
                  />
                }
                label="Orders"
                data-test="map-filter-orders-switch"
              />
              <FormControlLabel
                className={cx(styles.indentedFormControlLabel, styles.formControlSectionEnd)}
                classes={{
                  label: styles.checkboxControlLabel,
                  disabled: styles.controlLabelDisabled,
                }}
                control={
                  <Checkbox
                    checked={showJobsitesGeofences}
                    onChange={this.onChange('showJobsitesGeofences')}
                    disabled={!showJobsites}
                    color="primary"
                    classes={{
                      root: styles.checkboxBase,
                      disabled: styles.controlLabelDisabled,
                    }}
                  />
                }
                label="Show geofences"
                data-test="map-filter-jobsites-geofences-checkbox"
              />
              <div
                className={cx(
                  styles.datePickerContainer,
                  !showJobsites && styles.datePickerDisabled,
                )}
              >
                <TTDatePicker
                  dataTest="mapFilters-startDate-picker"
                  value={this.state.startDate}
                  label="Start Date"
                  onChangeCallback={this.onStartDateChange}
                  onBlurCallback={this.onStartDateBlur}
                  placeholder="MM/dd/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                />
              </div>
              <div
                className={cx(
                  styles.datePickerContainer,
                  !showJobsites && styles.datePickerDisabled,
                )}
              >
                <TTDatePicker
                  dataTest="mapFilters-endDate-picker"
                  value={this.state.endDate}
                  label="End Date"
                  onChangeCallback={this.onEndDateChange}
                  onBlurCallback={this.onEndDateBlur}
                  placeholder="MM/dd/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                />
                <span className={styles.endDateMessage}>*Maximum: 7 days from today</span>
              </div>
            </FormGroup>
          )}
          <Divider />
          <Button
            buttonClassName={cx(styles.resetButton, 'tt-btn-secondary')}
            onClick={this.onReset}
            name="Reset to defaults"
            dataTest="map-filter-reset-defaults"
          />
        </Popover>
      </div>
    );
  }
}

export interface MapFiltersDropdownProps {
  toggleMapFilter?: (toggle: any) => void;
  resetMapFilters?: () => void;
  setOpenMapFilters: (isOpen: boolean) => void;
  mapFilters: MapFilterState;
  currentRegion: RegionDto;
  currentUserUrl: string;
  selectedProductLine: string;
  userPermission: RegionAccessMap;
  variant?: 'incoming';
}

type MapFiltersPreferencesSource = Pick<
  MapFiltersDropdownProps,
  'currentRegion' | 'selectedProductLine' | 'mapFilters'
>;

export interface AnchorPosition {
  top: number;
  left: number;
}

export interface MapFiltersDropdownState {
  anchorElement: HTMLButtonElement | null;
  anchorReference: PopoverReference;
  anchorPosition: AnchorPosition;
  isOpen: boolean;
  startDate: Date;
  endDate: Date;
  userPreferences?: GeotraxUserPreferencesDto;
}

function mapStateToProps(state: ReduxState, ownProps: { variant?: 'incoming' }) {
  const isIncomingVariant = ownProps.variant === 'incoming';
  const plantList = isIncomingVariant
    ? incomingMapFilterPlantListSelector(state)
    : state.mapFilters.plantList;

  return {
    mapFilters: {
      ...state.mapFilters,
      plantList,
    },
    currentRegion: state.currentRegion,
    selectedProductLine: state.selectedProductLine,
    currentUserUrl: state.userUrl ?? '',
    userPermission: state.userPermission,
  };
}

export default connect(mapStateToProps, {
  toggleMapFilter,
  resetMapFilters,
  setOpenMapFilters,
})(MapFiltersDropdown);
